import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  UncontrolledTooltip,
  Table,
} from "reactstrap";
import Select from "react-select";
import { Link } from "react-router-dom";
import TableContainer from "../../../../components/Common/TableContainer";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { changePositionStatus, fetchDepartments, fetchDesignations, fetchPositions, savePosition, fetchEvaluations } from "store/actions";
import CustomSelect from "components/Common/custom-select";
import CustomDatePicker from "common/CustomDatePicker";
import Swal from "sweetalert2";
import Loader from "common/pageLoader";

const PositionCreation = () => {
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const { 
    positions, 
    designations,
    departments,
    evaluationsList,
    loading,
    success,
    error, 
  } = useSelector((state) => ({
    positions: state.JobSetupPositionSkillReducer?.positions ?? [],
    designations: state.DesignationMaster?.designations ?? [],
    departments: state.DepartmentMaster?.departments ?? [],
    evaluationsList: state.EvaluationMasterReducer?.evaluationsList ?? [],
    
    loading: state.JobSetupPositionSkillReducer?.loading || state.DesignationMaster?.loading || state.DepartmentMaster?.loading,
    error: state.JobSetupPositionSkillReducer?.error || state.DesignationMaster?.error || state.DepartmentMaster?.error,
    success: state.JobSetupPositionSkillReducer?.success,
  }));

  const levelOptions = [
    { value: 1, label: "LEVEL 1" },
    { value: 2, label: "LEVEL 2" },
    { value: 3, label: "LEVEL 3" },
    { value: 4, label: "LEVEL 4" },
    { value: 5, label: "LEVEL 5" },
    { value: 6, label: "LEVEL 6" },
  ]; 

  useEffect(() => {
        const authUserDetails = useAuthUserDetails();
        setAuthDetails(authUserDetails[0])
        const unitId = authUserDetails[0].unit_id
        dispatch(fetchPositions(unitId));
        dispatch(fetchDesignations(unitId))
        dispatch(fetchDepartments(unitId))
        dispatch(fetchEvaluations(unitId));
    }, [dispatch]);
  
  useEffect(() => {
      if(success){
        setIsEdit(false);
        setEditId(null);
        isEditRef.current = false;
        editIdRef.current = null;
        setLevelEvalRows([{ id: Date.now(), level: null, evaluationType: [] }]);
        formik.resetForm()
      }
  }, [success]);

  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const isEditRef = React.useRef(false);
  const editIdRef = React.useRef(null);
  const [levelEvalRows, setLevelEvalRows] = useState([{ id: Date.now(), level: null, evaluationType: [] }]);

  const handleAddLevelEvalRow = () => {
    setLevelEvalRows(prev => [...prev, { id: Date.now(), level: null, evaluationType: [] }]);
  };

  const handleLevelEvalChange = (id, field, value) => {
    setLevelEvalRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const handleRemoveLevelEvalRow = (id) => {
    setLevelEvalRows(prev => prev.filter(r => r.id !== id));
  };

  const handleEdit = (row) => {
    setIsEdit(true);
    setEditId(row.pos_id);
    isEditRef.current = true;
    editIdRef.current = row.pos_id;
    const departmentObj = departments.find(emp => String(emp.departmentId) === String(row.dep_id));
    const roleObj = designations.find(r => String(r.desig_id) === String(row.desig_id));
    const parseDate = (d) => {
      if (!d) return "";
      // API returns DD/MM/YYYY, convert to YYYY-MM-DD for dayjs
      const parts = d.split("/");
      if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
      return d;
    };
    formik.setValues({
      department: departmentObj ? { value: departmentObj.departmentId, label: departmentObj.departmentname } : null,
      role: roleObj ? { value: roleObj.desig_id, label: roleObj.designation } : null,
      positionCode: row.pos_code || "",
      position: row.position || "",
      fromDate: parseDate(row.from_date),
      toDate: parseDate(row.to_date),
    });
    const rows = (row.levels || []).map(lvl => {
      const levelObj = levelOptions.find(l => String(l.value) === String(lvl.level_id)) || null;
      const evalIds = String(lvl.evaluation_id || "").split(',').map(v => v.trim());
      const evalTypes = String(lvl.evaluation_type || "").split(',').map(v => v.trim());
      const evalObjs = evalIds.map((id, i) => ({ value: id, label: evalTypes[i] || id }));
      return { id: Date.now() + Math.random(), level: levelObj, evaluationType: evalObjs };
    });
    setLevelEvalRows(rows.length ? rows : [{ id: Date.now(), level: null, evaluationType: [] }]);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      department: null,
      role: null,
      positionCode: "",
      position: "",
      fromDate: "",
      toDate: "",
    },
    validationSchema: Yup.object({
      department: Yup.object().nullable().required("Please select a department"),
      role: Yup.object().nullable().required("Please select a role"),
      positionCode: Yup.string().required("Please enter a position code"),
      position: Yup.string().required("Please enter a position"),
      fromDate: Yup.string().required("Please select from date"),
      toDate: Yup.string().required("Please select to date"),
    }),
    onSubmit: (values, { resetForm }) => {
      if (levelEvalRows.length === 0) {
        Swal.fire('Warning', 'Please add at least one Level & Evaluation Type row', 'warning');
        return;
      }
      const posList = levelEvalRows.map(r => ({
        level_id: String(r.level?.value || ""),
        evaluation_type_id: (r.evaluationType || []).map(e => String(e.value))
      }));
      const payload = new FormData();
      payload.append("lst_pos_creation", JSON.stringify({ pos: posList }));
      payload.append("pos_id", isEditRef.current ? editIdRef.current : 0);  
      payload.append("dept_id", values.department?.value || "");   
      payload.append("role_id", values.role?.value || "");           
      payload.append("pos_code", values.positionCode); 
      payload.append("position", values.position); 
      payload.append("unit_id", authDetails?.unit_id || "");    
      payload.append("created_by", authDetails?.user_id || "");
      payload.append("from_date", values.fromDate || "");
      payload.append("to_date", values.toDate || "");       
      dispatch(savePosition(payload)) 
    },
  });

  const columns = [
   {
      Header: "S.No",
      Cell: ({ row }) => positions.length - row.index,
      className: "serial-no",
      applyHeaderClass: true,
    },
    {
      Header: "Action",
      Cell: (cellProps) => {
          const { b_active, pos_id } = cellProps.row.original;
          const isActive = b_active === 1;
          return (
              <div className="d-flex gap-3">
                  <Link
                      to="#"
                      className="text-success"
                      onClick={() => handleEdit(cellProps.row.original)}
                  >
                      <i className="bx bxs-edit font-size-18" id="edittooltip" />
                      <UncontrolledTooltip placement="top" target="edittooltip">
                          Edit
                      </UncontrolledTooltip>
                  </Link>
                  <Link
                      to="#"
                      style={{ color: isActive ? "green" : "red" }}
                      onClick={() => handleToggleStatus(pos_id, isActive)}
                  >
                      <i
                          className={`mdi ${isActive ? "mdi-check-circle" : "mdi-close-circle"} font-size-18`}
                          id={`status-tooltip-${pos_id}`}
                      />
                  </Link>
              </div>
          );
      },
  },
    { Header: "Department", accessor: "department" },
    { Header: "Role", accessor: "designation" },
    { Header: "Position Code", accessor: "pos_code" },
    { Header: "Position", accessor: "position" },
    {
      Header: "Level",
      accessor: "levels",
      Cell: ({ value }) => (value || []).map(l => l.level_id).join(", ") || "-",
    },
    {
      Header: "Evaluation Type",
      accessor: "levels",
      id: "eval_type",
      Cell: ({ value }) =>
        (value || []).map(l => l.evaluation_type).filter(Boolean).join(", ") || "-",
    },
  ];

const handleToggleStatus = (id,isActive) => {
      Swal.fire({
      title: 'Are you sure?',
      text: `You are about to ${isActive ? 'deactivate' : 'activate'} this.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      reverseButtons: false,
      dangerMode: true,
      }).then((result) => {
      if (result.isConfirmed) {
          const formData = new FormData();
          formData.append("mpos_id", id);
          formData.append("unit_id", authDetails?.unit_id);
          formData.append("create_by", authDetails?.user_id);
          dispatch(changePositionStatus(formData));
      }    
      });
  }; 
  const SelectcustomStyles = {
  control: (base) => ({ ...base, minHeight: 33.1 , maxHeight:33.1 }),
  dropdownIndicator: (base) => ({ ...base, padding: "2px 8px" }),
  clearIndicator: (base) => ({ ...base, padding: "2px 8px" }),
  valueContainer: (base) => ({ ...base, padding: "0px 8px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
};


  return (
    <div className="page-content">
      <Loader loading={loading}/>
      <Container fluid>
        <Breadcrumbs title="Job setup" breadcrumbItem="Position creation" />
        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit}>
              <Row className="mb-3 ">
                {/* Department */}
                <Col md="3">
                  <FormGroup>
                    <Label>Department <span className="text-danger">*</span></Label>
                    <CustomSelect
                      id="department"
                      name="department"
                      options={departments.map(emp => ({ value: emp.departmentId, label: emp.departmentname }))}
                      value={formik.values.department}
                      onChange={selectedOption => formik.setFieldValue("department", selectedOption)}
                      onBlur={() => formik.setFieldTouched("department", true)}  // ✅ Ensure onBlur is handled
                      className="hrms-select"
                      classNamePrefix="hrms-select"
                      isDisabled={isEdit}
                      styles={SelectcustomStyles}
                    />
                    {formik.touched.department && formik.errors.department && (
                      <div className="text-danger">{formik.errors.department}</div>
                    )}
                  </FormGroup>
                </Col>

                {/* Role */}
                <Col md="3">
                  <FormGroup>
                    <Label>Role <span className="text-danger">*</span></Label>
                    <CustomSelect
                      id="role"
                      name="role"
                      options={designations.map(emp => ({ value: emp.desig_id, label: emp.designation }))}
                      value={formik.values.role}
                      onChange={selectedOption => formik.setFieldValue("role", selectedOption)}
                      onBlur={() => formik.setFieldTouched("role", true)}
                      classNamePrefix="react-select"
                      isDisabled={isEdit}
                      styles={SelectcustomStyles}
                    />
                    {formik.touched.role && formik.errors.role && (
                      <div className="text-danger">{formik.errors.role}</div>
                    )}
                  </FormGroup>
                </Col>

                {/* Position Code */}
                <Col md="3">
                  <FormGroup>
                    <Label>Position Code <span className="text-danger">*</span></Label>
                    <Input
                      type="text"
                      id="positionCode"
                      name="positionCode"
                      value={formik.values.positionCode}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.positionCode && formik.errors.positionCode && (
                      <div className="text-danger">{formik.errors.positionCode}</div>
                    )}
                  </FormGroup>
                </Col>

                {/* Position */}
                <Col md="3">
                  <FormGroup>
                    <Label>Position <span className="text-danger">*</span></Label>
                    <Input
                      type="text"
                      id="position"
                      name="position"
                      value={formik.values.position}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.position && formik.errors.position && (
                      <div className="text-danger">{formik.errors.position}</div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <Row className="mb-3">
                {/* From Date */}
                <Col md="3">
                  <FormGroup>
                    <Label>From Date <span className="text-danger">*</span></Label>
                    <CustomDatePicker
                      name="fromDate"
                      value={formik.values.fromDate}
                      onChange={(val) => formik.setFieldValue("fromDate", val)}
                      onBlur={() => formik.setFieldTouched("fromDate", true)}
                      placeholder="Select Date"
                    />
                    {formik.touched.fromDate && formik.errors.fromDate && (
                      <div className="text-danger">{formik.errors.fromDate}</div>
                    )}
                  </FormGroup>
                </Col>

                {/* To Date */}
                <Col md="3">
                  <FormGroup>
                    <Label>To Date <span className="text-danger">*</span></Label>
                    <CustomDatePicker
                      name="toDate"
                      value={formik.values.toDate}
                      onChange={(val) => formik.setFieldValue("toDate", val)}
                      onBlur={() => formik.setFieldTouched("toDate", true)}
                      placeholder="Select Date"
                    />
                    {formik.touched.toDate && formik.errors.toDate && (
                      <div className="text-danger">{formik.errors.toDate}</div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              {/* Level & Evaluation Type Sub-Table */}
              <Row className="mb-2">
                <Col>
                  <Label className="mb-2 fw-semibold">Level &amp; Evaluation Type</Label>
                  <Table bordered size="sm" responsive>
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "40%" }}>Level <span className="text-danger">*</span></th>
                        <th style={{ width: "50%" }}>Evaluation Type <span className="text-danger">*</span></th>
                        <th style={{ width: "10%" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {levelEvalRows.map((row, index) => {
                        // Get the max level value selected in all rows before this one
                        const maxPrevLevel = levelEvalRows
                          .slice(0, index)
                          .reduce((max, r) => r.level ? Math.max(max, r.level.value) : max, 0);
                        // Levels already selected in OTHER rows
                        const usedLevels = levelEvalRows
                          .filter(r => r.id !== row.id && r.level)
                          .map(r => r.level.value);
                        const filteredLevelOptions = levelOptions.filter(
                          l => l.value > maxPrevLevel && !usedLevels.includes(l.value)
                        );
                        return (
                        <tr key={row.id}>
                          <td>
                            <CustomSelect
                              options={filteredLevelOptions}
                              value={row.level}
                              onChange={val => handleLevelEvalChange(row.id, "level", val)}
                              classNamePrefix="react-select"
                              styles={SelectcustomStyles}
                              placeholder="Select Level"
                            />
                          </td>
                          <td>
                            <CustomSelect
                              options={evaluationsList.map(item => ({ value: item.evaluation_id, label: item.evaluation_type }))}
                              value={row.evaluationType}
                              onChange={val => handleLevelEvalChange(row.id, "evaluationType", val)}
                              classNamePrefix="react-select"
                              styles={SelectcustomStyles}
                              isMulti
                              placeholder="Select Evaluation Type"
                            />
                          </td>
                          <td className="text-center">
                            <div className="d-flex align-items-center justify-content-center gap-2">
                              {levelEvalRows.length > 1 && (
                                <Link to="#" className="text-danger" onClick={() => handleRemoveLevelEvalRow(row.id)}>
                                  <i className="mdi mdi-delete font-size-18" />
                                </Link>
                              )}
                              {index === levelEvalRows.length - 1 && (
                                <Link to="#" className="text-success" onClick={handleAddLevelEvalRow}>
                                  <i className="bx bx-plus-circle font-size-18" />
                                </Link>
                              )}
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Col>
              </Row>

              <Row>
                <Col className="mob-center text-end">
                  <Button type="submit" className="btn button-save me-2 mt-2">
                    <i className="fas fa-save me-1" /> {isEdit ? "Update" : "Save"}
                  </Button>                  
                  <Button
                    type="button"
                    onClick={() => {
                      formik.handleReset();
                      setIsEdit(false);
                      setEditId(null);
                      isEditRef.current = false;
                      editIdRef.current = null;
                      setLevelEvalRows([{ id: Date.now(), level: null, evaluationType: [] }]);
                    }}
                    className="btn button-cancel mt-2"
                  ><i className="fas fa-sync me-1 " /> Cancel

                  </Button>

                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <TableContainer columns={columns} data={positions && positions.length > 0 ? positions : []} isGlobalFilter={true} customPageSize={10} isPagination={false} />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default PositionCreation;
